// 在列表中的表示是“块级元素”，如果结果为 true，就是 html5 的块级元素
import { isUnitlessNumber } from '@core/utils/cssProperty';

export const blockHTMLNode = {
	ADDRESS: false,
	ARTICLE: true,
	ASIDE: true,
	AUDIO: true,
	BLOCKQUOTE: false,
	CANVAS: true,
	DD: false,
	DIV: false,
	DL: false,
	FIELDSET: false,
	FIGCAPTION: true, // 图文信息组标题
	FIGURE: true, // 图文信息组
	FOOTER: true,
	FORM: false,
	H1: false,
	H2: false,
	H3: false,
	H4: false,
	H5: false,
	H6: false,
	HEADER: true,
	HGROUP: true, // header group
	HR: false,
	NOSCRIPT: false,
	OL: false,
	OUTPUT: true, // form 输出
	P: false,
	PRE: false,
	SECTION: true,
	TABLE: false,
	TFOOT: false,
	UL: false,
	VIDEO: true,
};

const defaultFocusableHTMLNode = {
	INPUT: true,
	SELECT: true,
	TEXTAREA: true,
	A: true,
	BUTTON: true,
	IFRAME: true,
	VIDEO: true,
};
export const isFocusable = (elem) => {
	return (
		(elem.tabIndex != null && elem.tabIndex > -1) ||
		elem.getAttribute('contentEditable') ||
		defaultFocusableHTMLNode[elem.nodeName]
	);
};

export const isWindow = (elem) => {
	const elementString = Object.prototype.toString.call(elem);
	return elementString === '[object Window]' || elementString === '[object global]';
};
export const isDocument = (elem) => {
	const { Document } = getWindow(elem);
	return elem instanceof Document;
};
export const isHTMLElement = (elem) => {
	if (isWindow(elem)) {
		return false;
	}
	return elem instanceof getWindow(elem).HTMLElement;
};
export const getDocument = (rootElement) => {
	const el = rootElement;
	return el && el.ownerDocument ? el.ownerDocument : document;
};
let _window = undefined;
// Note: Accessing "window" in IE11 is somewhat expensive, and calling "typeof window"
// hits a memory leak, whereas aliasing it and calling "typeof _window" does not.
// Caching the window value at the file scope lets us minimize the impact.
try {
	_window = window;
} catch (e) {
	/* no-op */
}
export const getWindow = (rootElement) => {
	if (!rootElement) {
		return _window;
	}
	const el = rootElement;
	return el && el.ownerDocument && el.ownerDocument.defaultView ? el.ownerDocument.defaultView : _window;
};

export const getParent = (child) => {
	return child && child.parentNode;
};

export const documentReady = function (callback) {
	///兼容FF,Google
	if (document.addEventListener) {
		document.addEventListener(
			'DOMContentLoaded',
			function () {
				callback();
			},
			false,
		);
	} else if (document.lastChild === document.body) {
		callback();
	}
};

export const getNodeFromSelector = (selector) => {
	const node = typeof selector === 'string' ? document.querySelector(selector) : selector;
	if (node) {
		return node;
	}
	throw new Error('Invalid selector', selector);
};

export const createNode = (parent, tag = 'div') => {
	const elem = document.createElement(tag);
	return parent.appendChild(elem);
};

export const removeNode = (node) => {
	if (!node) {
		return;
	}
	const { parentNode } = node;
	if (parentNode) {
		parentNode.removeChild(node);
	}
};

export const removeAllChildren = (node) => {
	while (node && node.firstChild) {
		node.removeChild(node.firstChild);
	}
};

export const isDescendant = (parent, child) => {
	let node = child.parentNode;

	while (node !== null) {
		if (node === parent) return true;
		node = node.parentNode;
	}
	return false;
};

export const getStyle = (node) => {
	if (!node) {
		return null;
	}
	return window.getComputedStyle(node, null);
};

export const getStyleValue = (nodeStyle, prop) => {
	const value = nodeStyle.getPropertyValue(prop);
	if (value === 'none' || !value) {
		return null;
	}
	return value;
};

// 找到父节点中第一个定位元素
export const findPositionedParent = (node, inclusive = false) => {
	function isPositioned(node) {
		const cs = getStyle(node);
		const pos = getStyleValue(cs, 'position');
		return pos && pos !== 'static';
	}

	if (!node) {
		return null;
	}

	if (inclusive && isPositioned(node)) {
		return node;
	}

	for (let parent = node.parentElement; parent !== null; parent = parent.parentElement) {
		if (isPositioned(parent)) {
			return parent;
		}
	}

	return document.documentElement;
};

export const addClass = (node, className) => {
	if (!node) {
		return null;
	}
	node.classList.add(`${className}`);
};
export const removeClass = (node, className) => {
	if (!node) {
		return null;
	}
	node.classList.remove(className);
};

function dangerousStyleValue(name, value, isCustomProperty) {
	// Note that we've removed escapeTextForBrowser() calls here since the
	// whole string will be escaped when the attribute is injected into
	// the markup. If you provide unsafe user data here they can inject
	// arbitrary CSS which may be problematic (I couldn't repro this):
	// https://www.owasp.org/index.php/XSS_Filter_Evasion_Cheat_Sheet
	// http://www.thespanner.co.uk/2007/11/26/ultimate-xss-css-injection/
	// This is not an XSS hole but instead a potential CSS injection issue
	// which has lead to a greater discussion about how we're going to
	// trust URLs moving forward. See #2115901

	const isEmpty = value == null || typeof value === 'boolean' || value === '';
	if (isEmpty) {
		return '';
	}

	if (
		!isCustomProperty &&
		typeof value === 'number' &&
		value !== 0 &&
		!(Object.prototype.hasOwnProperty.call(isUnitlessNumber, name) && isUnitlessNumber[name])
	) {
		return value + 'px'; // Presumes implicit 'px' suffix for unitless numbers
	}

	return ('' + value).trim();
}
export function setValueForStyles(node, styles) {
	const style = node.style;
	const keys = Object.keys(styles);
	for (let i = 0; i < keys.length; i += 1) {
		let styleName = keys[i];
		const isCustomProperty = styleName.indexOf('--') === 0;

		const styleValue = dangerousStyleValue(styleName, styles[styleName], isCustomProperty);
		if (styleName === 'float') {
			styleName = 'cssFloat';
		}
		if (isCustomProperty) {
			style.setProperty(styleName, styleValue);
		} else {
			style[styleName] = styleValue;
		}
	}
}

const SELECTOR =
	'a,frame,iframe,input:not([type=hidden]):not(:disabled),select:not(:disabled),textarea:not(:disabled),button:not(:disabled),*[tabindex]';
export const findFirstFocusableNode = (e) => {
	if (e.matches(SELECTOR)) {
		return e;
	}
	return e.querySelector(SELECTOR);
};

// 分析处理transform相关信息
export function parseTransform(transform) {
	if (transform.startsWith('matrix3d(')) {
		const transformArray = transform.slice(9, -1).split(/, /);

		return {
			x: +transformArray[12],
			y: +transformArray[13],
			scaleX: +transformArray[0],
			scaleY: +transformArray[5],
		};
	} else if (transform.startsWith('matrix(')) {
		const transformArray = transform.slice(7, -1).split(/, /);

		return {
			x: +transformArray[4],
			y: +transformArray[5],
			scaleX: +transformArray[0],
			scaleY: +transformArray[3],
		};
	}

	return null;
}
// 根据 transform 转换rect真实的显示位置信息
function inverseTransform(rect, transform, transformOrigin) {
	const parsedTransform = parseTransform(transform);
	if (!parsedTransform) {
		return rect;
	}
	const { scaleX, scaleY, x: translateX, y: translateY } = parsedTransform;

	const x = rect.left - translateX - (1 - scaleX) * parseFloat(transformOrigin);
	const y = rect.top - translateY - (1 - scaleY) * parseFloat(transformOrigin.slice(transformOrigin.indexOf(' ') + 1));
	const w = scaleX ? rect.width / scaleX : rect.width;
	const h = scaleY ? rect.height / scaleY : rect.height;

	return {
		width: w,
		height: h,
		top: y,
		right: x + w,
		bottom: y + h,
		left: x,
	};
}
export const getClientRect = (elem, options = { ignoreTransform: false }) => {
	let rect = elem.getBoundingClientRect();

	if (options.ignoreTransform) {
		const { transform, transformOrigin } = getWindow(elem).getComputedStyle(elem);

		if (transform) {
			rect = inverseTransform(rect, transform, transformOrigin);
		}
	}

	const { top, left, width, height, bottom, right } = rect;
	return {
		top,
		left,
		width,
		height,
		bottom,
		right,
	};
};

export const getRect = (elem) => {
	return getClientRect(elem, { ignoreTransform: true });
};
